import { CheckCircle2, FileText, Loader2, Sparkles } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { useBuilderStore } from "@/lib/builder/builder-store";
import { parseSpecMarkdown, hasClarification, type ParsedSpec } from "@/lib/builder/spec-parser";
import { cn } from "@/lib/utils";
import { generateAppSdd, getDebugSchema } from "@/services/debugpavilion";

import { useAiPanelStore, type AiPanelBridge } from "./ai-panel-store";

/** 单条文本：带 [NEEDS CLARIFICATION] 高亮。 */
function HighlightedText({ text }: { text: string }) {
  const parts = text.split(/(\[NEEDS CLARIFICATION:[^\]]*\])/g);
  return (
    <>
      {parts.map((part, idx) =>
        part.startsWith("[NEEDS CLARIFICATION:") ? (
          <mark
            key={idx}
            data-testid="spec-clarification"
            className="rounded bg-amber-500/20 px-1 py-0.5 text-amber-600 dark:text-amber-400"
          >
            {part}
          </mark>
        ) : (
          <span key={idx}>{part}</span>
        ),
      )}
    </>
  );
}

/** SPEC 字段分块渲染（Why 段落 + 四个列表字段）。 */
function SpecSections({ spec }: { spec: ParsedSpec }) {
  const blocks: { title: string; content: string[]; isList: boolean }[] = [
    { title: "Why", content: spec.why ? [spec.why] : [], isList: false },
    { title: "Capabilities", content: spec.capabilities, isList: true },
    { title: "Constraints", content: spec.constraints, isList: true },
    { title: "Non-goals", content: spec.nonGoals, isList: true },
    { title: "Success signal", content: spec.successSignal, isList: true },
  ];
  return (
    <div data-testid="plan-spec" className="space-y-3">
      {spec.name && (
        <h4 className="text-sm font-semibold text-primary"># SPEC.md — {spec.name}</h4>
      )}
      {blocks.map((block) => (
        <div key={block.title} className="rounded-md border bg-card p-3">
          <div className="mb-1.5 text-xs font-semibold text-muted-foreground">## {block.title}</div>
          {block.content.length === 0 ? (
            <div className="text-xs text-muted-foreground/70">（未定义）</div>
          ) : (
            <div className="space-y-1 text-sm">
              {block.content.map((item, idx) => (
                <div key={idx} className={cn("whitespace-pre-wrap", block.isList && "flex gap-1.5")}>
                  {block.isList && <span className="text-muted-foreground">-</span>}
                  <span>
                    <HighlightedText text={item} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {spec.clarifications.length > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
          <div className="mb-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
            {spec.clarifications.length} 处信息待确认
          </div>
          <ul className="list-inside list-disc space-y-0.5 text-xs text-muted-foreground">
            {spec.clarifications.map((question, idx) => (
              <li key={idx}>{question}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function PlanMode({ savedAppId, onAppCreated }: AiPanelBridge) {
  const plan = useAiPanelStore((state) => state.plan);
  const setPlan = useAiPanelStore((state) => state.setPlan);
  const setSchema = useBuilderStore((state) => state.setSchema);
  const [requirement, setRequirement] = React.useState("");
  const [confirmed, setConfirmed] = React.useState(false);

  const spec = plan?.specMd ? parseSpecMarkdown(plan.specMd) : null;
  const busy = plan?.status === "generating";

  const handleGenerate = async () => {
    if (!requirement.trim() || busy) return;
    setConfirmed(false);
    setPlan({ requirement, status: "generating" });
    try {
      const generation = await generateAppSdd({ requirement, sync: false });
      const appId = generation.app_id;
      if (!savedAppId && onAppCreated) onAppCreated(appId);
      const detail = await getDebugSchema(appId);
      setPlan({ requirement, status: "ready", appId, specMd: detail.spec_md ?? "" });
    } catch (error) {
      setPlan({ requirement, status: "error", error: String(error) });
    }
  };

  const handleConfirm = async () => {
    if (!plan?.appId || busy) return;
    setPlan({ ...plan, status: "generating" });
    try {
      const detail = await getDebugSchema(plan.appId);
      setSchema(detail.app_schema);
      setConfirmed(true);
      setPlan({ ...plan, status: "ready" });
    } catch (error) {
      setPlan({ ...plan, status: "error", error: String(error) });
    }
  };

  const hasClarifications = spec ? hasClarification(plan?.specMd ?? "") : false;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-3 overflow-auto p-3">
        <div className="rounded-md bg-muted/50 p-2.5 text-xs text-muted-foreground">
          规划模式：描述你的需求，AI 先生成 <span className="font-mono">SPEC.md</span>（Why / Capabilities /
          Constraints / Non-goals / Success signal）。确认后生成 AppSchema 并回填画布。
        </div>

        {plan?.status === "error" && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive">
            {plan.error}
          </div>
        )}

        {busy && (
          <div className="flex items-center gap-2 rounded-md border bg-card p-3 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            正在生成 SPEC.md 与 AppSchema...
          </div>
        )}

        {spec && !busy && (
          <>
            <SpecSections spec={spec} />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="flex-1"
                disabled={busy || hasClarifications}
                onClick={() => void handleConfirm()}
                data-testid="plan-confirm"
              >
                {confirmed ? (
                  <>
                    <CheckCircle2 className="mr-1.5 h-4 w-4" />
                    已应用，可在画布① 查看
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-1.5 h-4 w-4" />
                    确认并生成 AppSchema
                  </>
                )}
              </Button>
            </div>
            {hasClarifications && (
              <p className="text-[10px] text-amber-600 dark:text-amber-400">
                存在待澄清项，请先在需求中补充信息后重新生成。
              </p>
            )}
            {confirmed && (
              <p className="text-xs text-green-600 dark:text-green-400">
                AppSchema 已回填画布①，可切换到「无代码画布」继续编辑。
              </p>
            )}
          </>
        )}
      </div>

      <div className="border-t bg-card p-3">
        <Textarea
          value={requirement}
          onChange={(e) => setRequirement(e.target.value)}
          placeholder="描述你的应用需求，例如：创建一个内部工单系统，支持提交、分配与状态流转..."
          className="min-h-[64px] resize-none text-sm"
        />
        <Button
          className="mt-2 w-full"
          size="sm"
          disabled={busy || !requirement.trim()}
          onClick={() => void handleGenerate()}
          data-testid="plan-generate"
        >
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />}
          {busy ? "生成中..." : "生成 SPEC.md"}
        </Button>
      </div>
    </div>
  );
}
